
import React, { useState, useEffect, Fragment, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async'
import { toast } from 'components/Shared/Toast'
import { useQuery, useQueryClient, useMutation } from 'react-query'
import clsx from 'clsx';
import {
    Button, Input,
} from 'reactstrap';

import bootbox from 'components/Modals/Bootbox';
import apiClient, { fetcher } from 'api/client';
import { noop, getMongoObjectId } from 'utils'
import { useHistory } from 'react-router-dom';
import { updateSearch as updateSearchApi, markMetadataInPdf, addDocument, analyzeDocument, deleteDocument, fetchSearch, fetchSearchSuggestions } from 'api/actions/search';
import { fetchContract, fetchContractList, createContract } from 'api/actions/contracts';
import AnalysisViewerModal, { SuggestionList } from './AnalysisViewerModal';
import { RadialChart } from 'react-vis';
import TratoLoader from 'components/Shared/TratoLoader';
import ItemDetail from 'components/Shared/ItemDetail';
import { reactSelectStyles } from 'components/Shared/ReactSelectStyles'


const tabs = [{
    name: 'properties',
    icon: 'list',
    Component: PropertiesTab
}, {
    name: 'documents',
    icon: 'folder',
    Component: DocumentTab
}, {
    name: 'analysis',
    icon: 'eye',
    Component: AnalysisTab
}]

function SearchDetail({ isOpen, onClose, initialSearch = {}, onSearchUpdate }) {
    const [currentTab, setCurrentTab] = useState('properties');
    const [search, setSearch] = useState(initialSearch)

    const { status, data, error, isFetching } = useQuery(['search', initialSearch._id], (_, searchId) => fetcher(fetchSearch(searchId)), {
        initialData: initialSearch,
        staleTime: 5000,
        enabled: !!initialSearch._id
    })

    useEffect(() => {
        if (data) {
            setSearch(data)
        }
    }, [data])

    const handleChangeTab = (tabName) => {
        setCurrentTab(tabName)
    }

    const updateSearch = (search) => {
        const isFunc = search instanceof Function;
        if (isFunc) {
            setSearch(search)
        } else {
            setSearch(currentSearch => ({
                ...currentSearch,
                ...search
            }))
        }
    }

    return (
        <ItemDetail
            isOpen={isOpen}
            title={search && search.name}
            onClose={onClose}
            tabs={tabs}
            defaultTab="properties"
            search={search}
            updateSearch={updateSearch}
            onSearchUpdate={onSearchUpdate}
        />
    )
}

function PropertiesTab({ search, onSearchUpdate }) {
    const { t } = useTranslation()
    const queryClient = useQueryClient();
    const [conditions, setConditions] = useState([])
    const [name, setName] = useState(search.name)
    const isNewActive = useMemo(() => {
        if (conditions?.length) {
            return conditions?.some(condition => condition.isNew)
        }
        return false;
    }, [conditions])
    const { mutate: mutateUpdateSearch } = useMutation(({ searchId, ...payload }) => fetcher(updateSearchApi(searchId, payload)), {
        onMutate: ({ searchId, ...payload }) => {
            queryClient.cancelQueries('searchs')
            queryClient.cancelQueries(['searchs', searchId])

            const previousListValue = queryClient.getQueryData('searchs')
            const previousValue = queryClient.getQueryData(['searchs', searchId])

            queryClient.setQueryData('searchs', old => old.map(item => {
                if (item._id === searchId) {
                    return {
                        ...item,
                        ...payload
                    };
                }
                return item
            }))
            queryClient.setQueryData(['search', search._id], old => ({
                ...old,
                ...payload
            }))

            return { previousListValue, previousValue }
        },
        onError: (err, variables, previous) => {
            queryClient.setQueryData('searchs', previous.previousListValue);
            queryClient.setQueryData(['searchs', variables.searchId], previous.previousValue);
        },
        onSettled: (data, error, variables) => {
            queryClient.invalidateQueries('searchs')
            queryClient.invalidateQueries(['search', variables.searchId])
        },
    })

    // TODO: implement react hook form
    useEffect(() => {
        setConditions(search?.conditions?.map?.(condition => ({ id: getMongoObjectId(), value: condition, isNew: false })) ?? [])
    }, [search.conditions])

    useEffect(() => {
        setName(search?.name ?? '')
    }, [search.name])

    const handleSearchNameChange = (evt) => {
        setName(evt.target.value)
    }

    const handleAddCondition = () => {
        setConditions(currentConditons => ([
            ...currentConditons,
            { id: getMongoObjectId(), value: t('metadata.new_condition'), isNew: true }
        ]))
    }

    const handleConditionChange = ({ id, newName }) => {
        setConditions(currentConditons => {
            const newConditions = currentConditons.map(condition => {
                if (condition.id === id) {
                    return {
                        ...condition,
                        value: newName,
                        isNew: false,
                    }
                }
                return condition
            })
            handleSave(null, { conditions: newConditions })
            return newConditions
        })

    }

    const handleDeleteCondition = ({ id }) => {
        setConditions(currentConditons =>
            currentConditons.filter(condition => condition.id !== id)
        )
    }

    const handleSave = (_, overridePayload) => {

        let payload = {
            name
        }
        if (overridePayload) {
            payload.conditions = overridePayload.conditions.map(condition => condition.value)
        } else {
            payload.conditions = conditions.map(condition => condition.value)
        }

        mutateUpdateSearch({ searchId: search._id, ...payload });

        // if (data._id) {
        //     queryClient.setQueryData('searchs', old => old.map(item => {
        //         if (item._id === data._id) {
        //             return data;
        //         }
        //         return item
        //     }))
        //     queryClient.setQueryData(['search', search._id], data)
        // }
    }

    return (
        <div className="d-flex flex-column h-100 justify-content-between p-2 overflow-auto">
            <div>
                <p className="text-justify">
                    {t('metadata.configure_conditions')}
                </p>
                <div className="form-group">
                    <label>{t('workflow.new.name')}:</label>
                    <input
                        type="text"
                        className="form-control {{#is isOwner false}}not-allowed{{/is}}"
                        id="workflowname"
                        placeholder={t('workflow.new.name_placeholder')}
                        value={name}
                        defaultValue={search.name}
                        onChange={handleSearchNameChange}
                    />
                </div>

                <div className="form-group mb-0">
                    <label>{t('metadata.conditions')}:</label>
                </div>
                <div className="overflow-auto">
                    {
                        conditions?.map((condition, i) => (
                            <ConditionalItem
                                key={condition.id}
                                number={i + 1}
                                condition={condition}
                                onSave={handleConditionChange}
                                onDelete={handleDeleteCondition}
                            />
                        ))
                    }
                </div>
            </div>


            <div className="p-2">
                <Button className="w-100 mt-2 mb-2" onClick={handleAddCondition} disabled={isNewActive}>
                    <i className="fas fa-plus"></i> {t('metadata.add_condition')}
                </Button>
                <Button className="w-100" color="primary" onClick={handleSave}>
                    <i className="fas fa-save text-white"></i> {t('workflow.new.save')}
                </Button>
            </div>
        </div>
    )
}



export function ConditionalItem({ number, condition, onDelete = noop, onSave = noop }) {
    const [isEditable, setIsEditable] = useState(condition.isNew)
    const nameInputRef = useRef(true)


    const toggleIsEditable = () => {
        setIsEditable(editable => !editable)
    }

    const handleSave = () => {
        onSave({ id: condition.id, newName: nameInputRef.current.value })
        setIsEditable(false)
    }

    const handleDelete = () => {
        onDelete({ id: condition.id })
    }

    return (
        <div className="d-flex border">
            <div className="font-weight-bold text-center border-right px-2 py-2 text-secondary">
                {number}
            </div>
            <div className="text-dark align-self-center px-2 w-100">
                {
                    isEditable ?
                        <input ref={nameInputRef} className="form-control" defaultValue={condition.value} autoFocus />
                        :
                        condition.value
                }
            </div>
            {isEditable ? (
                <Fragment>
                    <span className="p-2" onClick={handleSave}>
                        <i className="fas fa-check text-success"></i>
                    </span>
                    <span className="delete-step p-2" onClick={toggleIsEditable}>
                        <i className="fas fa-times text-danger"></i>
                    </span>
                </Fragment>
            ) : (
                <Fragment>
                    <span className="p-2" onClick={toggleIsEditable}>
                        <i className="fas fa-edit text-info"></i>
                    </span>
                    <span className="delete-step p-2" onClick={handleDelete}>
                        <i className="fas fa-times text-danger"></i>
                    </span>
                </Fragment>
            )}

        </div>
    )
}

function DocumentTab({ search = {}, updateSearch }) {
    const { t } = useTranslation()
    const history = useHistory();
    const queryClient = useQueryClient();
    const [list, setList] = useState(search.documents);
    const [status, setStatus] = useState('')
    const selectRef = useRef(null);
    const [selectedDocument, setSelectedDocument] = useState({})
    const [documentsAnalyzing, setDocumentsAnalyzing] = useState([])
    const [isViewerOpen, setIsViewerOpen] = useState(false)
    const onError = useCallback((error) => toast.error(t(`errors.${error.message}`)), [t])
    const { mutate: mutateAddDocument } = useMutation((payload) => fetcher(addDocument(payload)), {
        onError,
        onSuccess: (data) => {
            queryClient.setQueryData(['search', search._id], old => ({
                ...old,
                documents: data.documents
            }))
        }
    })
    const { mutate: mutateAnalyzeDocument } = useMutation((payload) => fetcher(analyzeDocument(payload)), {
        onError,
        onSuccess: (data) => {
            if (data.success) {
                const documentModified = data.data.documentModified;
                queryClient.setQueryData(['search', search._id], old => ({
                    ...old,
                    documents: old.documents.map(currentDoc => {
                        if (currentDoc._id === documentModified._id) {
                            return documentModified
                        }

                        return currentDoc
                    })
                }))
            }

            setDocumentsAnalyzing([])
        }
    })
    const { mutate: mutateCreateContract } = useMutation((payload) => fetcher(createContract(payload)), {
        onError,
        onSuccess: (data) => {
            history.push(`/contracts/editor/${data._id}`);
        }
    })
    const { mutate: mutateDeleteDocument } = useMutation((payload) => fetcher(deleteDocument(payload)), {
        onError,
        onSuccess: (data, { documentId }) => {
            if (data.success) {
                queryClient.setQueryData(['search', search._id], old => ({
                    ...old,
                    documents: old.documents.filter(d => d._id !== documentId)
                }))
            }
        }
    })

    useEffect(() => {
        // setStatus('loading')
        // const fetchDocuments = async () => {
        //     const {status, data } = await apiClient(findByConditions({ searchId: search._id }));
        //     console.log({status, data})
        //     setList(data)
        //     setStatus('ready')
        // }
        // fetchDocuments()
    }, [search])

    useEffect(() => {
        setList(search.documents)
    }, [search.documents])

    const hasDocumentsToAnalyze = useMemo(() => {
        return list?.some?.(item => !item.isAnalized) ?? false
    }, [list])

    const handleAnalyzeAll = () => {
        const documentsToAnalyze = list.filter(item => !item.isAnalized)

        setDocumentsAnalyzing(documentsToAnalyze.map(item => item._id))

        for (const doc of documentsToAnalyze) {
            handleAnalyzeDocument(doc)
        }

    }

    const handleDocumentSelected = ({ value }) => {
        mutateAddDocument({
            searchId: search._id,
            contractId: value
        })
    }
    const handleAnalyzeDocument = (doc) => {
        mutateAnalyzeDocument({
            searchId: search._id,
            documentId: doc._id
        })
    }

    const handleCreateContract = () => {
        mutateCreateContract({
            search: search._id,
            name: `Contrato generado (${search.name})`,
            participants: [
                {
                    obligation: 'individual',
                    name: '',
                    last_name: '',
                    representative: '',
                    phone: '',
                    email: '',
                    label: 'La parte',
                },
                {
                    obligation: 'individual',
                    name: '',
                    last_name: '',
                    representative: '',
                    phone: '',
                    email: '',
                    label: 'La contraparte',
                }
            ],
            tags: []
        })

    }

    const handleDeleteDocument = (doc) => {
        mutateDeleteDocument({
            searchId: search._id,
            documentId: doc._id
        })
    }

    const handleViewAnalysis = (doc) => {
        setSelectedDocument(doc)
        setIsViewerOpen(true)
    }

    async function fetchContracts(inputValue) {
        try {
            // TODO: only show contracts not selected
            const res = await apiClient(fetchContractList({
                limit: 20,
                search: `name|${inputValue}`,
                filter: 'with_metadata',
                url: '/contracts/all',
                // searchMetadataId: search._id
            }))
            return res.data.docs.map((doc) => {
                return { value: doc._id, label: doc.name }
            })
        } catch (error) {
            console.error(error)
        }
    }


    return (
        <div className="d-flex flex-column h-100 justify-content-between p-2 overflow-auto">
            <div>
                <h4>{t('metadata.contracts_to_analyse')}</h4>
                <AsyncSelect
                    cacheOptions
                    defaultOptions
                    styles={reactSelectStyles} 
                    className="mb-2"
                    ref={selectRef}
                    loadOptions={fetchContracts}
                    onChange={handleDocumentSelected}
                    placeholder={t('metadata.add_document')}
                    noOptionsMessage={() => <div>{t('metadata.no_options')}</div>}
                />
                {
                    status === 'loading' ?
                        <div>{t('metadata.uploading')}... </div>
                        :
                        <Fragment>

                            {
                                list?.map?.(item => (
                                    <DocumentItem
                                        document={item}
                                        isAnalyzing={documentsAnalyzing.includes(item._id)}
                                        totalConditions={search?.conditions?.length}
                                        onAnalyzeDocument={handleAnalyzeDocument}
                                        onDelete={handleDeleteDocument}
                                        onViewAnalysis={handleViewAnalysis}
                                    />
                                ))
                            }
                            {
                                !list?.length &&
                                <div>{t('metadata.no_related_documents')}</div>
                            }
                        </Fragment>
                }
            </div>
            <div className="p-2 text-center">
                <small className="text-muted">{t('metadata.create_document')}</small>
                <Button className="w-100 mt-2 mb-2" disabled={!hasDocumentsToAnalyze} onClick={handleAnalyzeAll}>
                    {t('metadata.analyze')}
                </Button>
                <Button className="w-100" color="primary" onClick={handleCreateContract}>
                    <i className="fas fa-plus"></i> {t('metadata.create')}
                </Button>

            </div>
            <AnalysisViewerModal
                isOpen={isViewerOpen}
                onClose={() => setIsViewerOpen(false)}
                selectedDocument={selectedDocument}
                search={search}
            />

        </div>
    )
}

function DocumentItem({ document: doc, isAnalyzing, totalConditions, onAnalyzeDocument, onDelete, onViewAnalysis }) {
    const [status, setStatus] = useState('');
    const { t } = useTranslation()
    const handleAnalyzeDocument = () => {
        setStatus('analyzing')
        // TODO FIX AWAIT
        onAnalyzeDocument(doc)
        setStatus('')

    }

    const handleDelete = () => {
        if (status !== 'analyzing') {
            onDelete(doc)
        }
    }

    const handleViewAnalysis = () => {
        onViewAnalysis(doc)
    }

    useEffect(() => {
        if (isAnalyzing) {
            setStatus('analyzing')
        } else {
            setStatus('')
        }
    }, [isAnalyzing])


    const wordsAnalizedCount = useMemo(() => {
        return doc.wordsAnalized.reduce((count, wa) => {
            if (wa.locations?.length > 0) {
                count++;
            }
            return count
        }, 0)
    }, [doc.wordsAnalized])

    const chartData = useMemo(() => {
        return [
            { angle: doc.isAnalized ? (wordsAnalizedCount || 0) : 0, radius: 1.4, innerRadius: 1.8, color: 1 },
            { angle: (totalConditions - wordsAnalizedCount) || 0, radius: 1.4, innerRadius: 1.8, color: 2 }
        ]
    }, [wordsAnalizedCount, totalConditions, doc.isAnalized])


    return (
        <div className="d-flex align-items-center mb-2 ">
            <div className="position-relative bg-light shadow-sm rounded border d-flex w-100 align-items-center">
                <div className="text-secondary w-100 pr-1 py-2 pl-3 border-right">
                    <small className="text-muted">ID: {doc?.contract?.documentId}</small>
                    <div>{doc?.contract?.name}</div>
                    {/* <span className="text-primary hover_text-decoration-underline" onClick={handleMarkPdf(item.contract)}>Ver</span> */}
                </div>
                <div className="d-flex align-items-center justify-content-center flex-column align-self-center text-primary p-2 text-center" style={{ width: 80 }}>
                    {
                        status === 'analyzing' ?
                            <TratoLoader className="mb-2" size="md" />
                            : <RadialChart height={45} width={45} data={chartData} colorRange={['#F47B33', '#e1e3e6']} />
                    }
                    <div className="mt-n3 ">
                        {doc.isAnalized ?
                            <small>{wordsAnalizedCount}/{totalConditions}</small>
                            :
                            <small style={{ fontSize: 9 }}>
                                {status === 'analyzing' ? t('metadata.analyzing') : t('metadata.pending')}
                            </small>
                        }
                    </div>
                </div>
            </div>
            <div className="px-3 text-center">
                <div className={clsx(doc.isAnalized ? 'text-primary' : 'text-muted opacity-2')} onClick={handleViewAnalysis}>
                    <i className="fas fa-eye text-secondary"></i>
                </div>
                <div className={clsx(status === 'analyzing' ? 'text-muted opacity-2' : 'text-muted')} onClick={handleDelete}>
                    <i className="fas fa-times"></i>
                </div>

            </div>

        </div>
    )
}



function AnalysisTab({ search }) {
    const { t } = useTranslation()

    return (
        <div className="d-flex flex-column h-100 p-2 overflow-auto">
            <SuggestionList
                searchId={search._id}
                showOrigin
                contentHeight={100}
            />
        </div>
    )
}

export default SearchDetail;